/**
 * ProcessBuilderDetail Component
 *
 * Detailed view of a single process with visual flow,
 * execution history, statistics, and version management.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Edit,
  Copy,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  GitBranch,
  History,
  Activity,
  Settings,
  Database,
  Zap,
  Mail,
  FileText,
  Users,
  Send,
  Workflow
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardModal
} from '../../standard';
import type {
  Process,
  ProcessExecution,
  ProcessCriteriaGroup,
  ProcessAction,
  ScheduledAction,
  ActionType
} from '../../../types/processBuilder';

// ============================================
// INLINE BADGE COMPONENT
// ============================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'secondary';
  size?: 'sm' | 'md';
}

const Badge: React.FC<BadgeProps> = ({ children, variant = 'secondary', size = 'md' }) => {
  const variantClasses = {
    success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  return (
    <span className={`inline-flex items-center font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]}`}>
      {children}
    </span>
  );
};

// ============================================
// INLINE TABS COMPONENT
// ============================================

interface TabsProps {
  tabs: { id: string; label: string }[];
  activeTab: string;
  onTabChange: (id: string) => void;
}

const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange }) => (
  <div className="border-b border-gray-200 dark:border-gray-700">
    <nav className="flex gap-4">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
            activeTab === tab.id
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  </div>
);

// ============================================
// INTERFACES
// ============================================

interface ProcessBuilderDetailProps {
  process: Process;
  executions?: ProcessExecution[];
  onBack: () => void;
  onEdit: (process: Process) => void;
  onClone: (process: Process) => void;
  onDelete: (process: Process) => void;
  onActivate: (process: Process) => void;
  onDeactivate: (process: Process) => void;
  onRefreshExecutions?: () => void;
}

// ============================================
// CONSTANTS
// ============================================

const ACTION_ICONS: Record<ActionType, React.ReactNode> = {
  field_update: <Database size={14} />,
  create_record: <FileText size={14} />,
  update_related: <Database size={14} />,
  send_email: <Mail size={14} />,
  post_to_feed: <Send size={14} />,
  create_task: <FileText size={14} />,
  submit_for_approval: <Users size={14} />,
  call_apex: <Zap size={14} />,
  invoke_process: <Workflow size={14} />
};

const ACTION_LABELS: Record<ActionType, string> = {
  field_update: 'Update Fields',
  create_record: 'Create Record',
  update_related: 'Update Related',
  send_email: 'Send Email',
  post_to_feed: 'Post to Feed',
  create_task: 'Create Task',
  submit_for_approval: 'Submit for Approval',
  call_apex: 'Call Apex',
  invoke_process: 'Invoke Process'
};

const TRIGGER_LABELS: Record<string, string> = {
  record_created: 'When a record is created',
  record_updated: 'When a record is edited',
  record_created_or_updated: 'When a record is created or edited',
  platform_event: 'On platform event',
  invocable: 'When invoked by another process'
};

// ============================================
// HELPER COMPONENTS
// ============================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'gray';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    gray: 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

interface ExpandableSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultExpanded?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  icon,
  defaultExpanded = false,
  badge,
  children
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-gray-500 dark:text-gray-400">{icon}</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
          {badge}
        </div>
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      {expanded && (
        <div className="p-4 bg-white dark:bg-gray-900">
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================
// CRITERIA DISPLAY COMPONENT
// ============================================

interface CriteriaDisplayProps {
  criteria: ProcessCriteriaGroup;
}

const CriteriaDisplay: React.FC<CriteriaDisplayProps> = ({ criteria }) => {
  if (!criteria.conditions || criteria.conditions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No conditions defined (always true)
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Logic: {criteria.conditionLogic === 'CUSTOM' ? criteria.customLogic : criteria.conditionLogic}
      </div>
      {criteria.conditions.map((condition, index) => (
        <div
          key={condition.id || index}
          className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm"
        >
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400 w-5">
            {index + 1}
          </span>
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {condition.field}
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {condition.operator.replace(/_/g, ' ')}
          </span>
          {condition.value !== null && condition.value !== undefined && (
            <span className="font-mono text-gray-900 dark:text-gray-100">
              "{String(condition.value)}"
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// ACTIONS DISPLAY COMPONENT
// ============================================

interface ActionsDisplayProps {
  actions: ProcessAction[];
  title?: string;
}

const ActionsDisplay: React.FC<ActionsDisplayProps> = ({ actions, title }) => {
  if (!actions || actions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No actions configured
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {title && (
        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
          {title}
        </h5>
      )}
      {actions.map((action, index) => (
        <div
          key={action.id || index}
          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
        >
          <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
            {ACTION_ICONS[action.type]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {action.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {ACTION_LABELS[action.type]}
              {action.description && ` • ${action.description}`}
            </p>
          </div>
          {action.isActive === false && (
            <Badge variant="secondary">Disabled</Badge>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// SCHEDULED ACTIONS DISPLAY
// ============================================

interface ScheduledActionsDisplayProps {
  scheduledActions: ScheduledAction[];
}

const ScheduledActionsDisplay: React.FC<ScheduledActionsDisplayProps> = ({ scheduledActions }) => {
  if (!scheduledActions || scheduledActions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No scheduled actions configured
      </p>
    );
  }

  const formatSchedule = (sa: ScheduledAction): string => {
    const unit = sa.scheduleType.includes('days') ? 'days' :
                 sa.scheduleType.includes('hours') ? 'hours' : 'minutes';
    const direction = sa.scheduleType.includes('before') ? 'before' : 'after';
    return `${sa.scheduleValue} ${unit} ${direction} ${sa.baseDateFieldLabel || sa.baseDateField}`;
  };

  return (
    <div className="space-y-4">
      {scheduledActions.map((sa, index) => (
        <div
          key={sa.id || index}
          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-purple-600 dark:text-purple-400" />
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {sa.name}
              </span>
              {sa.isActive === false && (
                <Badge variant="secondary">Disabled</Badge>
              )}
            </div>
            <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
              {formatSchedule(sa)}
            </p>
          </div>
          <div className="p-3">
            <ActionsDisplay actions={sa.actions} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// EXECUTION HISTORY COMPONENT
// ============================================

interface ExecutionHistoryProps {
  executions: ProcessExecution[];
  onRefresh?: () => void;
}

const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({ executions, onRefresh }) => {
  const [selectedExecution, setSelectedExecution] = useState<ProcessExecution | null>(null);

  const statusIcons: Record<string, React.ReactNode> = {
    completed: <CheckCircle size={16} className="text-green-500" />,
    failed: <XCircle size={16} className="text-red-500" />,
    running: <RefreshCw size={16} className="text-blue-500 animate-spin" />,
    partial: <AlertTriangle size={16} className="text-yellow-500" />
  };

  const formatDuration = (ms?: number): string => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (executions.length === 0) {
    return (
      <div className="text-center py-8">
        <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No executions recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing {executions.length} recent executions
        </p>
        {onRefresh && (
          <StandardButton size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={14} className="mr-1" />
            Refresh
          </StandardButton>
        )}
      </div>

      <div className="space-y-2">
        {executions.slice(0, 10).map((exec) => (
          <div
            key={exec.id}
            className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            onClick={() => setSelectedExecution(exec)}
          >
            <div className="flex-shrink-0">{statusIcons[exec.status]}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {exec.recordName || exec.recordId}
                </span>
                {exec.matchedCriteriaGroupName && (
                  <Badge variant="info" size="sm">
                    {exec.matchedCriteriaGroupName}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(exec.startTime)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDuration(exec.duration)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {exec.executedActions.length} actions
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <StandardModal
          isOpen={true}
          onClose={() => setSelectedExecution(null)}
          title="Execution Details"
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Record</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {selectedExecution.recordName || selectedExecution.recordId}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                <div className="flex items-center gap-2">
                  {statusIcons[selectedExecution.status]}
                  <span className="capitalize">{selectedExecution.status}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Started</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(selectedExecution.startTime)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {formatDuration(selectedExecution.duration)}
                </p>
              </div>
            </div>

            {selectedExecution.errorMessage && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {selectedExecution.errorMessage}
                </p>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Executed Actions
              </h4>
              <div className="space-y-2">
                {selectedExecution.executedActions.map((action, index) => (
                  <div
                    key={action.actionId || index}
                    className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    {action.status === 'success' ? (
                      <CheckCircle size={14} className="text-green-500" />
                    ) : action.status === 'failed' ? (
                      <XCircle size={14} className="text-red-500" />
                    ) : (
                      <AlertTriangle size={14} className="text-yellow-500" />
                    )}
                    <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
                      {action.actionName}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDuration(action.duration)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </StandardModal>
      )}
    </div>
  );
};

// ============================================
// VERSION HISTORY COMPONENT
// ============================================

interface VersionHistoryProps {
  process: Process;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({ process }) => {
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  if (!process.versions || process.versions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No version history available
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {process.versions.map((version) => (
        <div
          key={version.id}
          className={`flex items-center gap-4 p-3 rounded-lg ${
            version.status === 'active'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-gray-50 dark:bg-gray-800'
          }`}
        >
          <div className="flex-shrink-0">
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
              v{version.versionNumber}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  version.status === 'active' ? 'success' :
                  version.status === 'draft' ? 'warning' :
                  'secondary'
                }
                size="sm"
              >
                {version.status}
              </Badge>
              {version.description && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {version.description}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Created {formatDate(version.createdDate)} by {version.createdBy}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ProcessBuilderDetail: React.FC<ProcessBuilderDetailProps> = ({
  process,
  executions = [],
  onBack,
  onEdit,
  onClone,
  onDelete,
  onActivate,
  onDeactivate,
  onRefreshExecutions
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Calculate stats
  const stats = useMemo(() => {
    const total = executions.length;
    const successful = executions.filter(e => e.status === 'completed').length;
    const failed = executions.filter(e => e.status === 'failed').length;
    const avgDuration = total > 0
      ? Math.round(executions.reduce((sum, e) => sum + (e.duration || 0), 0) / total)
      : 0;

    return {
      total,
      successful,
      failed,
      successRate: total > 0 ? Math.round((successful / total) * 100) : 0,
      avgDuration
    };
  }, [executions]);

  // Get all immediate actions
  const allImmediateActions = useMemo(() => {
    const actions: ProcessAction[] = [];
    Object.values(process.immediateActions || {}).forEach(arr => {
      actions.push(...arr);
    });
    return actions;
  }, [process.immediateActions]);

  // Get all scheduled actions
  const allScheduledActions = useMemo(() => {
    const actions: ScheduledAction[] = [];
    Object.values(process.scheduledActions || {}).forEach(arr => {
      actions.push(...arr);
    });
    return actions;
  }, [process.scheduledActions]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'criteria', label: 'Criteria & Actions' },
    { id: 'history', label: 'Execution History' },
    { id: 'versions', label: 'Versions' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <StandardButton variant="ghost" onClick={onBack}>
            <ArrowLeft size={20} />
          </StandardButton>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {process.name}
              </h1>
              <Badge
                variant={process.isActive ? 'success' : process.status === 'draft' ? 'warning' : 'secondary'}
              >
                {process.isActive ? 'Active' : process.status === 'draft' ? 'Draft' : 'Inactive'}
              </Badge>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                v{process.currentVersion}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {process.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {process.isActive ? (
            <StandardButton
              variant="secondary"
              onClick={() => onDeactivate(process)}
              leftIcon={<Pause size={16} />}
            >
              Deactivate
            </StandardButton>
          ) : (
            <StandardButton
              variant="primary"
              onClick={() => onActivate(process)}
              leftIcon={<Play size={16} />}
            >
              Activate
            </StandardButton>
          )}
          <StandardButton
            variant="secondary"
            onClick={() => onEdit(process)}
            leftIcon={<Edit size={16} />}
          >
            Edit
          </StandardButton>
          <StandardButton
            variant="secondary"
            onClick={() => onClone(process)}
            leftIcon={<Copy size={16} />}
          >
            Clone
          </StandardButton>
          <StandardButton
            variant="ghost"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={16} className="text-red-500" />
          </StandardButton>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              label="Total Executions"
              value={process.executionCount || stats.total}
              icon={<Activity size={20} />}
              color="blue"
            />
            <StatCard
              label="Successful"
              value={process.successCount || stats.successful}
              icon={<CheckCircle size={20} />}
              color="green"
            />
            <StatCard
              label="Failed"
              value={process.failureCount || stats.failed}
              icon={<XCircle size={20} />}
              color="red"
            />
            <StatCard
              label="Success Rate"
              value={`${stats.successRate}%`}
              icon={<Activity size={20} />}
              color={stats.successRate >= 90 ? 'green' : stats.successRate >= 70 ? 'yellow' : 'red'}
            />
          </div>

          {/* Process Info */}
          <StandardCard>
            <StandardCard.Header
              title="Process Information"
              action={<Settings size={20} className="text-gray-400" />}
            />
            <StandardCard.Body>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Object</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{process.objectType}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trigger</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {TRIGGER_LABELS[process.triggerType] || process.triggerType}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{process.category || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">API Name</p>
                  <p className="font-mono text-sm text-gray-900 dark:text-gray-100">{process.apiName || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(process.createdDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Modified</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(process.lastModifiedDate).toLocaleDateString()}
                    {process.lastModifiedByName && ` by ${process.lastModifiedByName}`}
                  </p>
                </div>
              </div>
            </StandardCard.Body>
          </StandardCard>

          {/* Quick Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StandardCard>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {process.criteriaGroups?.length || 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Decision Groups</p>
              </div>
            </StandardCard>
            <StandardCard>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {allImmediateActions.length}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Immediate Actions</p>
              </div>
            </StandardCard>
            <StandardCard>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {allScheduledActions.length}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Scheduled Actions</p>
              </div>
            </StandardCard>
          </div>
        </div>
      )}

      {activeTab === 'criteria' && (
        <div className="space-y-4">
          {/* Entry Criteria */}
          <ExpandableSection
            title="Entry Criteria"
            icon={<GitBranch size={18} />}
            defaultExpanded={true}
            badge={
              <Badge variant="info" size="sm">
                {process.entryCriteria?.conditions?.length || 0} conditions
              </Badge>
            }
          >
            {process.entryCriteria ? (
              <CriteriaDisplay criteria={process.entryCriteria} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                No entry criteria - process runs for all records
              </p>
            )}
          </ExpandableSection>

          {/* Decision Groups */}
          {process.criteriaGroups?.map((group, index) => (
            <ExpandableSection
              key={group.id || index}
              title={group.name || `Decision Group ${index + 1}`}
              icon={<GitBranch size={18} />}
              badge={
                <div className="flex items-center gap-2">
                  <Badge variant="info" size="sm">
                    {group.conditions?.length || 0} conditions
                  </Badge>
                  <Badge variant="secondary" size="sm">
                    {(process.immediateActions?.[group.id] || []).length} actions
                  </Badge>
                </div>
              }
            >
              <div className="space-y-4">
                {group.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {group.description}
                  </p>
                )}

                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Conditions
                  </h5>
                  <CriteriaDisplay criteria={group} />
                </div>

                {process.immediateActions?.[group.id]?.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Immediate Actions
                    </h5>
                    <ActionsDisplay actions={process.immediateActions[group.id]} />
                  </div>
                )}

                {process.scheduledActions?.[group.id]?.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Scheduled Actions
                    </h5>
                    <ScheduledActionsDisplay scheduledActions={process.scheduledActions[group.id]} />
                  </div>
                )}
              </div>
            </ExpandableSection>
          ))}

          {(!process.criteriaGroups || process.criteriaGroups.length === 0) && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <GitBranch size={48} className="mx-auto mb-3 opacity-50" />
              <p>No decision groups configured</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <StandardCard>
          <StandardCard.Header
            title="Execution History"
            action={<History size={20} className="text-gray-400" />}
          />
          <StandardCard.Body>
            <ExecutionHistory
              executions={executions}
              onRefresh={onRefreshExecutions}
            />
          </StandardCard.Body>
        </StandardCard>
      )}

      {activeTab === 'versions' && (
        <StandardCard>
          <StandardCard.Header
            title="Version History"
            action={<History size={20} className="text-gray-400" />}
          />
          <StandardCard.Body>
            <VersionHistory process={process} />
          </StandardCard.Body>
        </StandardCard>
      )}

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Process"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <strong>{process.name}</strong>?
            This action cannot be undone.
          </p>
          {process.isActive && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Warning: This process is currently active. Deleting it will stop all future executions.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <StandardButton variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </StandardButton>
            <StandardButton
              variant="danger"
              onClick={() => {
                onDelete(process);
                setShowDeleteModal(false);
              }}
            >
              Delete
            </StandardButton>
          </div>
        </div>
      </StandardModal>
    </div>
  );
};

export default ProcessBuilderDetail;
